import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScheduleStatus } from '../../../queries/scheduleStatus';

/**
 * "Now playing … · Next … at HH:MM" banner for a wall or group (Phase 6 UX).
 * Reads the schedule-status endpoint so a restaurant owner always sees what is
 * active and what's coming — no guessing why a playlist did/didn't switch.
 */
const SOURCE_ICON = { window: 'pi-calendar', default: 'pi-bookmark', fallback: 'pi-clock', none: 'pi-ban' };

export default function ScheduleStatusBanner({ type = 'wall', id, compact = false }) {
  const { t } = useTranslation();
  const { data, isLoading } = useScheduleStatus({ type, id });
  if (!id) return null;

  const schedule = data?.schedule;
  const now = schedule?.now;
  const next = schedule?.next;
  const uniform = data?.uniform ?? true;

  const sourceLabel = (s) => t(`video_wall.schedule.source_${s || 'none'}`);

  return (
    <div className="p-3 mb-3" style={{
      background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)',
      borderRadius: 'var(--pp-radius-lg)', borderInlineStart: '4px solid var(--pp-primary)',
    }}>
      {isLoading ? (
        <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.schedule.status_loading')}</small>
      ) : !now || now.source === 'none' ? (
        <div>
          <div style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>
            <i className="pi pi-ban me-2" style={{ color: 'var(--pp-warning)' }} />
            {t('video_wall.schedule.now_nothing')}
          </div>
          {next && (
            <small style={{ color: 'var(--pp-text-secondary)' }}>
              {t('video_wall.schedule.next_is', { name: next.playlist_name || '—', at: next.at, day: t(`video_wall.schedule.day_${next.at_day}`, next.at_day) })}
            </small>
          )}
        </div>
      ) : (
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div style={{ minWidth: 0 }}>
            <div className="text-truncate" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>
              <i className={`pi ${SOURCE_ICON[now.source] || 'pi-play'} me-2`} style={{ color: 'var(--pp-success)' }} />
              {t('video_wall.schedule.now_playing', { name: now.playlist_name || '—' })}
              <span className="ms-2" style={{ fontWeight: 500, fontSize: 12, color: 'var(--pp-text-secondary)' }}>
                {now.window ? `${sourceLabel(now.source)} · ${now.window.start}–${now.window.end}` : sourceLabel(now.source)}
              </span>
            </div>
            {next ? (
              <small style={{ color: 'var(--pp-text-secondary)' }}>
                <i className="pi pi-arrow-right me-1" style={{ fontSize: 10 }} />
                {t('video_wall.schedule.next_is', { name: next.playlist_name || '—', at: next.at, day: t(`video_wall.schedule.day_${next.at_day}`, next.at_day) })}
              </small>
            ) : (
              <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.schedule.next_none')}</small>
            )}
          </div>
          {!compact && schedule?.timezone && (
            <small style={{ color: 'var(--pp-text-secondary)' }}>
              <i className="pi pi-clock me-1" />{schedule.timezone}
            </small>
          )}
        </div>
      )}
      {type === 'group' && !uniform && (
        <small className="d-block mt-1" style={{ color: 'var(--pp-warning)' }}>
          <i className="pi pi-exclamation-triangle me-1" />{t('video_wall.schedule.group_not_uniform')}
        </small>
      )}
    </div>
  );
}
